import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import EmailDeliverabilityPage from './components/services/EmailDeliverabilityPage';
import EmailMarketingPage from './components/services/EmailMarketingPage';
import EmailAuditingPage from './components/services/EmailAuditingPage';
import SEOPage from './components/services/SEOPage';
import SEOAuditPage from './components/services/SEOAuditPage';
import LinkedinMarketingPage from './components/services/LinkedinMarketingPage';
import LinkedinOptimisationPage from './components/services/LinkedinOptimisationPage';
import IQDMarcProductPage from './components/products/IQDMarcProductPage';
import Spam0ProductPage from './components/products/Spam0ProductPage';
import ScrollToTop from './components/ScrollToTop';
import { HelmetProvider } from 'react-helmet-async';
import ContactPage from './components/ContactPage';
import SEOForWebsite from './components/SEOForWebsite';
import BookMeetingPage from './components/BookMeetingPage';

// Import tool pages
import DNSSuperTool from './components/tools/DNSSuperTool';
import SPFAnalyzer from './components/tools/SPFAnalyzer';
import DMARCAnalyzer from './components/tools/DMARCAnalyzer';
import DKIMAnalyzer from './components/tools/DKIMAnalyzer';
import MXAnalyzer from './components/tools/MXAnalyzer';
import EmailTestPage from './components/tools/EmailTestPage';
import EmailTestResultPage from './components/tools/EmailTestResultPage';

// Import consulting pages
import InstantlyPage from './components/consulting/InstantlyPage';
import SmartleadPage from './components/consulting/SmartleadPage';
import KlaviyoPage from './components/consulting/KlaviyoPage';
import GoHighLevelPage from './components/consulting/GoHighLevelPage';
import MailWizzPage from './components/consulting/MailWizzPage';
import MauticPage from './components/consulting/MauticPage';
import ListmonkPage from './components/consulting/ListmonkPage';
import PhpListPage from './components/consulting/PhpListPage';
import MailCowPage from './components/consulting/MailCowPage';
import PowerMTAPage from './components/consulting/PowerMTAPage';
import PostalPage from './components/consulting/PostalPage';
import PostfixPage from './components/consulting/PostfixPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0B60B1',
      light: '#4d8bc9',
      dark: '#094a8f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#094a8f',
      light: '#4d8bc9',
      dark: '#063a6f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#f7fafd',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router basename="/">
          <ScrollToTop />
          <SEOForWebsite 
            title="ImpactQuill - Email Deliverability Solutions"
            description="Enterprise email deliverability solutions ensuring your business-critical emails reach the inbox. Expert setup, monitoring, and support for SPF, DKIM, DMARC, and more."
            keywords="email deliverability, SPF, DKIM, DMARC, email authentication, inbox placement, blacklist monitoring"
            canonical="/"
          />
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '100vh',
          }}>
            <Navbar />
            <Box component="main" sx={{ 
              flexGrow: 1,
              pt: { xs: 7, sm: 8 },
              pb: { xs: 4, sm: 6 },
            }}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/services/email-deliverability" element={<EmailDeliverabilityPage />} />
                <Route path="/services/email-marketing" element={<EmailMarketingPage />} />
                <Route path="/services/email-audit" element={<EmailAuditingPage />} />
                <Route path="/services/seo" element={<SEOPage />} />
                <Route path="/services/seo-audit" element={<SEOAuditPage />} />
                <Route path="/services/linkedin-marketing" element={<LinkedinMarketingPage />} />
                <Route path="/services/linkedin-optimization" element={<LinkedinOptimisationPage />} />
                <Route path="/products/iqdmarc" element={<IQDMarcProductPage />} />
                <Route path="/products/spam0" element={<Spam0ProductPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/book-meeting" element={<BookMeetingPage />} />

                {/* Tool Routes */}
                <Route path="/tools/dns-super-tool" element={<DNSSuperTool />} />
                <Route path="/tools/spf-analyzer" element={<SPFAnalyzer />} />
                <Route path="/tools/dmarc-analyzer" element={<DMARCAnalyzer />} />
                <Route path="/tools/dkim-analyzer" element={<DKIMAnalyzer />} />
                <Route path="/tools/mx-analyzer" element={<MXAnalyzer />} />
                <Route path="/tools/email-test" element={<EmailTestPage />} />
                <Route path="/tools/email-test-result/:id" element={<EmailTestResultPage />} />

                {/* Consulting Routes */}
                <Route path="/consulting/instantly" element={<InstantlyPage />} />
                <Route path="/consulting/smartlead" element={<SmartleadPage />} />
                <Route path="/consulting/klaviyo" element={<KlaviyoPage />} />
                <Route path="/consulting/gohighlevel" element={<GoHighLevelPage />} />
                <Route path="/consulting/mailwizz" element={<MailWizzPage />} />
                <Route path="/consulting/mautic" element={<MauticPage />} />
                <Route path="/consulting/listmonk" element={<ListmonkPage />} />
                <Route path="/consulting/phplist" element={<PhpListPage />} />
                <Route path="/consulting/mailcow" element={<MailCowPage />} />
                <Route path="/consulting/powermta" element={<PowerMTAPage />} />
                <Route path="/consulting/postal" element={<PostalPage />} />
                <Route path="/consulting/postfix" element={<PostfixPage />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App; 